import { Box } from '@mui/material'
import { useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'

import Card from '@/components/Card'
import ColorSelection from '@/components/ColorSelection'
import LabelWithTooltip from '@/components/LabelWithTooltip'
import Select from '@/components/Select'
import { useAppSelector } from '@/store'
import { useGetMetadataQuery } from '@/store/apiService'
import { LABColor } from '@/store/appSlice'
import {
  GINGIVA_STAINING_REGIONS,
  setGingivaStainingBrand,
  setSelectedGingivaStainingKey,
} from '@/store/stainingStudio'
import { getOptions } from '@/utils/helper'

import type { Metadata } from '../../crown/StaingingStudio'

const GingivaStainingPanel = ({
  containerRef,
  regenerateRecipe,
}: {
  containerRef: React.RefObject<HTMLDivElement>
  regenerateRecipe: (regionKey: string, labColor: LABColor) => void
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'gingiva' })
  const { t: translate } = useTranslation()

  const dispatch = useDispatch()
  const { data: metadata } = useGetMetadataQuery(undefined, {
    refetchOnMountOrArgChange: true,
  }) as {
    data: Metadata | undefined
    error: any
    isLoading: boolean
  }

  const stainingBrands = useMemo(
    () =>
      metadata ? getOptions(metadata, 'available_gingivastainingbrands') : [],
    [metadata],
  )

  const gingivaStaing = useAppSelector(
    (state) => state.stainingStudio.gingivaStaing,
  )
  const selectedGingivaStainingKey = useAppSelector(
    (state) => state.stainingStudio.selectedGingivaStainingKey,
  )

  const gingivaStainingBrand = useAppSelector(
    (state) => state.stainingStudio.gingivastainingbrand,
  )
  const canvasImage = useAppSelector((state) => state.app.canvasImage)

  const gingivaTargetColor = useAppSelector(
    (state) => state.stainingStudio.gingivaTarget,
  )

  const { control, setValue, clearErrors } = useForm({
    defaultValues: {
      gingivastainingbrand: gingivaStainingBrand,
    },
    mode: 'onChange',
  })

  return (
    <Box
      display="flex"
      flexDirection="column"
      flexGrow={1}
      gap={1}
      overflow="auto"
    >
      <Card>
        <Select
          options={stainingBrands}
          title={t('gingivaStainingBrand')}
          tooltipText={t('gingivaStainingBrandTooltip')}
          control={control}
          name="gingivastainingbrand"
          onFormValueChange={(name, value) => {
            dispatch(setGingivaStainingBrand(value))
          }}
        />
      </Card>
      {Object.keys(GINGIVA_STAINING_REGIONS)?.map((r, i) => {
        const currentTarget = gingivaStaing?.[canvasImage?.id]?.[r]?.targetcolor
        const newTarget = gingivaTargetColor?.[canvasImage?.id]?.[r]

        const isSameTarget =
          !currentTarget || !newTarget
            ? true
            : currentTarget.length !== newTarget.length
              ? false
              : currentTarget.every((val, idx) => val === newTarget[idx])

        return (
          <Card key={i}>
            <LabelWithTooltip
              tooltipText={translate(
                GINGIVA_STAINING_REGIONS[
                  r as keyof typeof GINGIVA_STAINING_REGIONS
                ],
              )}
              label={translate(
                GINGIVA_STAINING_REGIONS[
                  r as keyof typeof GINGIVA_STAINING_REGIONS
                ],
              )}
            />
            <ColorSelection
              control={control}
              name={r}
              title={t('targetColor')}
              error={false}
              clearErrors={clearErrors}
              setValue={setValue}
              data={gingivaStaing?.[canvasImage?.id]?.[r]?.targetcolor}
              selectedPickerKey={selectedGingivaStainingKey}
              onclick={() => dispatch(setSelectedGingivaStainingKey(r))}
              containerRef={containerRef}
              snackbarClose={!selectedGingivaStainingKey}
              hasRecipe={!!gingivaStaing?.[canvasImage?.id]?.[r]?.output}
              regenerateRecipe={() =>
                regenerateRecipe(r, gingivaTargetColor?.[canvasImage?.id])
              }
              disableButton={isSameTarget}
            />
          </Card>
        )
      })}
    </Box>
  )
}

export default GingivaStainingPanel
